/**
 * Shrinks the Provenir splash SVG logo from its full container width down to
 * `--logo-max-width` as the user scrolls through the splash hero. Mirrors the
 * width of the header logo so the splash mark feels like it's flowing into the
 * sticky header.
 *
 * Also collapses the inner fixed `.splash` element's height pixel-for-pixel
 * with the scroll position (1px scrolled = 1px shorter) until it reaches 0,
 * so the splash recedes upward as the user scrolls past it. Targets the inner
 * `.splash` (which is `position: fixed`) rather than the section wrapper so
 * the scroll runway stays intact and document height is unaffected.
 */

const STORE = 'provenirshop';
const LOGO_VAR = '--logo-max-width';

function parseCssLength(value, contextEl) {
  if (!value) return 0;
  const trimmed = String(value).trim();
  if (!trimmed) return 0;

  const probe = document.createElement('div');
  probe.style.cssText = `position:absolute;visibility:hidden;pointer-events:none;width:${trimmed};`;
  contextEl.appendChild(probe);
  const px = probe.getBoundingClientRect().width;
  contextEl.removeChild(probe);
  return px;
}

function setupSplash(splash) {
  const svg = splash.querySelector('svg');

  let startWidth = 0;
  let targetWidth = 0;
  let startHeight = 0;
  let splashTop = 0;
  let splashHeight = 0;
  let ticking = false;

  const measure = () => {
    if (svg) svg.style.width = '';
    splash.style.height = '';

    const rect = splash.getBoundingClientRect();
    const styles = getComputedStyle(splash);
    const paddingLeft = parseFloat(styles.paddingLeft) || 0;
    const paddingRight = parseFloat(styles.paddingRight) || 0;

    startWidth = Math.max(0, rect.width - paddingLeft - paddingRight);
    startHeight = rect.height || window.innerHeight;
    splashTop = rect.top + (window.pageYOffset || 0);
    splashHeight = rect.height || window.innerHeight;

    const root = document.documentElement;
    const cssValue = getComputedStyle(root).getPropertyValue(LOGO_VAR);
    targetWidth = parseCssLength(cssValue, root);

    update();
  };

  const update = () => {
    ticking = false;

    const scrollY = window.pageYOffset || document.documentElement.scrollTop || 0;

    if (startHeight) {
      const nextHeight = Math.max(0, startHeight - scrollY);
      splash.style.height = `${nextHeight}px`;
    }

    if (!svg || !startWidth || !targetWidth) return;

    const distance = Math.max(1, splashHeight);
    const raw = (scrollY - splashTop) / distance;
    const progress = Math.min(1, Math.max(0, raw));

    const width = startWidth + (targetWidth - startWidth) * progress;
    svg.style.width = `${width}px`;
  };

  const onScroll = () => {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(update);
  };

  let resizeFrame = 0;
  const onResize = () => {
    cancelAnimationFrame(resizeFrame);
    resizeFrame = requestAnimationFrame(measure);
  };

  measure();

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', onResize);
  window.addEventListener('load', measure);
}

function init() {
  if (document.documentElement.dataset.store !== STORE) return;

  const splashes = document.querySelectorAll('.splash');
  if (!splashes.length) return;

  splashes.forEach(setupSplash);
}

if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
}
